import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, errorText, formatQty } from '../api';
import { useAuth } from '../auth';
import {
  Badge,
  Card,
  CheckField,
  Empty,
  Loading,
  Notice,
  NumberField,
  TextField,
} from '../components/ui';
import type { Location, Warehouse, Zone } from '../types';

export function LocationsPage() {
  const { can } = useAuth();
  const mayEdit = can('admin', 'kaufmann', 'disponent');

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [locations, setLocations] = useState<Location[] | null>(null);
  const [warehouseId, setWarehouseId] = useState('');
  const [sp] = useSearchParams();
  const [search, setSearch] = useState(sp.get('q') ?? '');
  useEffect(() => {
    const q = sp.get('q');
    if (q !== null) setSearch(q);
  }, [sp]);
  const [onlyEmpty, setOnlyEmpty] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const [showWarehouseForm, setShowWarehouseForm] = useState(false);
  const [newWarehouse, setNewWarehouse] = useState({ code: '', name: '', city: '' });

  const [showRangeForm, setShowRangeForm] = useState(false);
  const [range, setRange] = useState<{ prefix: string; rows: number | ''; levels: number | ''; zoneId: string }>({
    prefix: 'A',
    rows: 6,
    levels: 4,
    zoneId: '',
  });

  const [editWh, setEditWh] = useState<Warehouse | null>(null);
  const [whEdit, setWhEdit] = useState({ code: '', name: '', city: '', active: true });

  const [editLoc, setEditLoc] = useState<Location | null>(null);
  const [locZones, setLocZones] = useState<Zone[]>([]);
  const [locEdit, setLocEdit] = useState<{
    code: string;
    name: string;
    type: string;
    capacity: number | '';
    zoneId: string;
    active: boolean;
  }>({ code: '', name: '', type: 'lager', capacity: '', zoneId: '', active: true });

  const loadWarehouses = useCallback(async () => {
    setWarehouses(await api<Warehouse[]>('/api/warehouse'));
  }, []);

  const load = useCallback(async () => {
    setError('');
    try {
      const [locationList, zoneList] = await Promise.all([
        api<Location[]>('/api/warehouse/locations', {
          query: { warehouseId, search, onlyEmpty: onlyEmpty ? 'true' : undefined },
        }),
        api<Zone[]>('/api/warehouse/zones', { query: { warehouseId } }),
      ]);
      setLocations(locationList);
      setZones(zoneList);
    } catch (err) {
      setError(errorText(err));
    }
  }, [warehouseId, search, onlyEmpty]);

  useEffect(() => {
    loadWarehouses().catch((err) => setError(errorText(err)));
  }, [loadWarehouses]);

  useEffect(() => {
    const timer = setTimeout(load, search ? 250 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  async function createWarehouse() {
    setBusy(true);
    setError('');
    try {
      await api('/api/warehouse', { method: 'POST', body: newWarehouse });
      setMessage(`Lager ${newWarehouse.code} angelegt - inklusive der drei Gegenbuchungsplätze.`);
      setNewWarehouse({ code: '', name: '', city: '' });
      setShowWarehouseForm(false);
      await loadWarehouses();
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function createRange() {
    if (!warehouseId) return setError('Bitte zuerst ein Lager auswählen.');
    setBusy(true);
    setError('');
    try {
      const result = await api<{ created: number }>('/api/warehouse/locations/range', {
        method: 'POST',
        body: {
          warehouseId,
          zoneId: range.zoneId || null,
          prefix: range.prefix,
          rows: range.rows === '' ? 1 : range.rows,
          levels: range.levels === '' ? 1 : range.levels,
        },
      });
      setMessage(`${result.created} Lagerplätze angelegt.`);
      setShowRangeForm(false);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  function beginEditWh(w: Warehouse) {
    setEditWh(w);
    setWhEdit({ code: w.code, name: w.name, city: w.city ?? '', active: w.active });
  }

  async function saveWh() {
    if (!editWh) return;
    setBusy(true);
    setError('');
    try {
      await api(`/api/warehouse/${editWh.id}`, {
        method: 'PUT',
        body: { code: whEdit.code, name: whEdit.name, city: whEdit.city || null, active: whEdit.active },
      });
      setMessage(`Lager ${whEdit.code} gespeichert.`);
      setEditWh(null);
      await loadWarehouses();
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function beginEditLoc(l: Location) {
    setEditLoc(l);
    setLocEdit({
      code: l.code,
      name: l.name ?? '',
      type: l.type,
      capacity: l.capacity_units ?? '',
      zoneId: l.zone_id ?? '',
      active: l.active,
    });
    try {
      setLocZones(await api<Zone[]>('/api/warehouse/zones', { query: { warehouseId: l.warehouse_id } }));
    } catch {
      setLocZones([]);
    }
  }

  async function saveLoc() {
    if (!editLoc) return;
    setBusy(true);
    setError('');
    try {
      await api(`/api/warehouse/locations/${editLoc.id}`, {
        method: 'PUT',
        body: {
          code: locEdit.code,
          name: locEdit.name || null,
          type: locEdit.type,
          capacityUnits: locEdit.capacity === '' ? null : Number(locEdit.capacity),
          zoneId: locEdit.zoneId || null,
          exclusiveOwnerId: editLoc.exclusive_owner_id,
          active: locEdit.active,
        },
      });
      setMessage(`Lagerplatz ${locEdit.code} gespeichert.`);
      setEditLoc(null);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const LOC_TYPEN = ['lager', 'wareneingang', 'warenausgang', 'kommissionierung', 'sperrlager'];

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      {editWh && (
        <Card title={`Lager bearbeiten — ${editWh.code}`}>
          <div className="form-grid">
            <TextField label="Kürzel" value={whEdit.code} onChange={(v) => setWhEdit({ ...whEdit, code: v })} />
            <TextField label="Name" value={whEdit.name} onChange={(v) => setWhEdit({ ...whEdit, name: v })} />
            <TextField label="Ort" value={whEdit.city} onChange={(v) => setWhEdit({ ...whEdit, city: v })} />
          </div>
          <CheckField label="Aktiv" checked={whEdit.active} onChange={(v) => setWhEdit({ ...whEdit, active: v })} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="primary" onClick={saveWh} disabled={busy}>Speichern</button>
            <button onClick={() => setEditWh(null)}>Abbrechen</button>
          </div>
        </Card>
      )}

      {editLoc && (
        <Card title={`Lagerplatz bearbeiten — ${editLoc.warehouse_code} / ${editLoc.code}`}>
          <div className="form-grid">
            <TextField label="Platzcode" value={locEdit.code} onChange={(v) => setLocEdit({ ...locEdit, code: v })} />
            <TextField label="Name (optional)" value={locEdit.name} onChange={(v) => setLocEdit({ ...locEdit, name: v })} />
            <label className="field">
              <span>Typ</span>
              <select value={locEdit.type} onChange={(e) => setLocEdit({ ...locEdit, type: e.target.value })}>
                {LOC_TYPEN.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </label>
            <NumberField
              label="Kapazität (Einheiten, optional)"
              value={locEdit.capacity}
              onChange={(v) => setLocEdit({ ...locEdit, capacity: v })}
              min={0}
            />
            <label className="field">
              <span>Zone (optional)</span>
              <select value={locEdit.zoneId} onChange={(e) => setLocEdit({ ...locEdit, zoneId: e.target.value })}>
                <option value="">keine Zone</option>
                {locZones.map((zone) => (
                  <option key={zone.id} value={zone.id}>{zone.code} · {zone.name}</option>
                ))}
              </select>
            </label>
          </div>
          <CheckField label="Aktiv (nicht gesperrt)" checked={locEdit.active} onChange={(v) => setLocEdit({ ...locEdit, active: v })} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="primary" onClick={saveLoc} disabled={busy}>Speichern</button>
            <button onClick={() => setEditLoc(null)}>Abbrechen</button>
          </div>
        </Card>
      )}

      <Card
        title="Lager"
        actions={
          mayEdit && (
            <button className="small" onClick={() => setShowWarehouseForm(!showWarehouseForm)}>
              {showWarehouseForm ? 'Abbrechen' : 'Neues Lager'}
            </button>
          )
        }
        padded={false}
      >
        {showWarehouseForm && (
          <div className="body" style={{ borderBottom: '1px solid var(--line)' }}>
            <div className="form-grid">
              <TextField
                label="Kürzel"
                value={newWarehouse.code}
                onChange={(v) => setNewWarehouse({ ...newWarehouse, code: v })}
                hint="z. B. DD1"
              />
              <TextField
                label="Name"
                value={newWarehouse.name}
                onChange={(v) => setNewWarehouse({ ...newWarehouse, name: v })}
              />
              <TextField
                label="Ort"
                value={newWarehouse.city}
                onChange={(v) => setNewWarehouse({ ...newWarehouse, city: v })}
              />
            </div>
            <button className="primary" onClick={createWarehouse} disabled={busy}>
              Lager anlegen
            </button>
          </div>
        )}

        {warehouses.length === 0 ? (
          <Empty>Noch kein Lager angelegt.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Kürzel</th>
                <th>Name</th>
                <th>Ort</th>
                <th className="num">Lagerplätze</th>
                <th>Status</th>
                {mayEdit && <th></th>}
              </tr>
            </thead>
            <tbody>
              {warehouses.map((warehouse) => (
                <tr key={warehouse.id}>
                  <td className="mono">{warehouse.code}</td>
                  <td>{warehouse.name}</td>
                  <td className="small">{warehouse.city ?? '—'}</td>
                  <td className="num">{warehouse.location_count}</td>
                  <td>{warehouse.active ? <Badge kind="ok">aktiv</Badge> : <Badge kind="bad">inaktiv</Badge>}</td>
                  {mayEdit && (
                    <td className="num">
                      <button className="small ghost" onClick={() => beginEditWh(warehouse)}>bearbeiten</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <div className="toolbar">
        <label className="field">
          <span>Lager</span>
          <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            <option value="">alle Lager</option>
            {warehouses.map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.code} · {warehouse.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field" style={{ minWidth: 220 }}>
          <span>Suche</span>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Platzcode" />
        </label>
        <CheckField label="Nur freie Plätze" checked={onlyEmpty} onChange={setOnlyEmpty} />
        <span className="spacer" />
        {mayEdit && (
          <button className="primary" onClick={() => setShowRangeForm(!showRangeForm)}>
            Plätze nach Schema anlegen
          </button>
        )}
      </div>

      {showRangeForm && (
        <Card title="Lagerplätze nach Schema">
          <p className="small muted" style={{ marginTop: 0 }}>
            Erzeugt Plätze im Format PRÄFIX-REIHE-EBENE, also z. B. A-01-01 bis A-06-04.
          </p>
          <div className="form-grid">
            <TextField label="Präfix" value={range.prefix} onChange={(v) => setRange({ ...range, prefix: v })} />
            <NumberField
              label="Reihen"
              value={range.rows}
              onChange={(v) => setRange({ ...range, rows: v })}
              min={1}
            />
            <NumberField
              label="Ebenen"
              value={range.levels}
              onChange={(v) => setRange({ ...range, levels: v })}
              min={1}
            />
            <label className="field">
              <span>Zone (optional)</span>
              <select value={range.zoneId} onChange={(e) => setRange({ ...range, zoneId: e.target.value })}>
                <option value="">keine Zone</option>
                {zones.map((zone) => (
                  <option key={zone.id} value={zone.id}>
                    {zone.code} · {zone.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <button className="primary" onClick={createRange} disabled={busy}>
            {busy ? 'Anlegen …' : 'Plätze anlegen'}
          </button>
        </Card>
      )}

      <Card title="Lagerplätze" padded={false}>
        {!locations ? (
          <Loading />
        ) : locations.length === 0 ? (
          <Empty>Keine Lagerplätze gefunden.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Lager</th>
                  <th>Platz</th>
                  <th>Zone</th>
                  <th>Typ</th>
                  <th className="num">Artikel</th>
                  <th className="num">Bestand</th>
                  <th>Status</th>
                  {mayEdit && <th></th>}
                </tr>
              </thead>
              <tbody>
                {locations.map((location) => (
                  <tr key={location.id}>
                    <td className="mono small">{location.warehouse_code}</td>
                    <td className="mono">{location.code}</td>
                    <td className="small">{location.zone_code ?? '—'}</td>
                    <td className="small">{location.type}</td>
                    <td className="num">{location.article_count}</td>
                    <td className="num">{formatQty(location.used_qty)}</td>
                    <td>
                      {!location.active ? (
                        <Badge kind="bad">gesperrt</Badge>
                      ) : location.article_count === 0 ? (
                        <Badge>frei</Badge>
                      ) : (
                        <Badge kind="ok">belegt</Badge>
                      )}
                    </td>
                    {mayEdit && (
                      <td className="num">
                        <button className="small ghost" onClick={() => beginEditLoc(location)}>bearbeiten</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
